/**
 * SimulationCacheService — cache du résultat d'analyse/simulation par hash
 *
 * Objectif (lot #2 « test honnête ») : éviter de relancer un vrai appel IA quand
 * les questions n'ont pas changé. Le cache est invalidé UNIQUEMENT au changement
 * des entrées (questions + objectif + contexte + volume + useGemini) — pas de TTL.
 *
 * ⚠️ Cache client-side (localStorage) : garde-fou UX/coût, pas une sécurité.
 */

import type {
  SimulationConfig,
  SimulationResult,
} from "../../types/simulation";

const CACHE_KEY = "simulation_cache_v1";
const MAX_ENTRIES = 20; // borne la taille du localStorage

interface CacheEntry {
  hash: string;
  result: SimulationResult;
  cachedAt: number; // epoch ms
}

/** Question telle que passée à simulate() (shape minimale utilisée pour le hash). */
interface HashableQuestion {
  title: string;
  type: string;
  required?: boolean;
  options?: Array<{ id: string; label: string }>;
  matrixRows?: Array<{ id: string; label: string }>;
  matrixColumns?: Array<{ id: string; label: string }>;
  matrixType?: string;
}

/**
 * Sérialise les questions de façon stable et indépendante des `id` générés
 * (deux éditions identiques au libellé près produisent le même hash).
 */
function normalizeQuestions(questions: HashableQuestion[]): string {
  return JSON.stringify(
    questions.map((q) => ({
      t: q.title.trim(),
      k: q.type,
      r: !!q.required,
      o: (q.options ?? []).map((o) => o.label.trim()),
      mr: (q.matrixRows ?? []).map((r) => r.label.trim()),
      mc: (q.matrixColumns ?? []).map((c) => c.label.trim()),
      mt: q.matrixType ?? null,
    })),
  );
}

/**
 * Hash DJB2 (déterministe, pas de dépendance crypto). Suffisant pour une clé
 * de cache local — pas un usage sécurité.
 */
function djb2(input: string): string {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 33) ^ input.charCodeAt(i);
  }
  // >>> 0 pour rester sur un entier non signé 32 bits
  return (hash >>> 0).toString(36);
}

/**
 * Calcule la clé de cache pour une paire (config, questions).
 * Toute modification d'une question, de l'objectif, du contexte, du volume ou de
 * `useGemini` change le hash → invalide le cache.
 */
export function hashSimulationInput(
  config: Pick<
    SimulationConfig,
    "volume" | "context" | "useGemini" | "objective"
  >,
  questions: HashableQuestion[],
): string {
  const payload = JSON.stringify({
    v: config.volume,
    c: config.context,
    g: !!config.useGemini,
    obj: (config.objective ?? "").trim(),
    q: normalizeQuestions(questions),
  });
  return djb2(payload);
}

function readCache(): CacheEntry[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CacheEntry[]) : [];
  } catch {
    return [];
  }
}

function writeCache(entries: CacheEntry[]): void {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify(entries.slice(0, MAX_ENTRIES)),
    );
  } catch {
    // localStorage plein / indisponible → on ignore (le cache est best-effort)
  }
}

/** Renvoie le résultat caché pour ce hash, ou null. */
export function getCachedResult(hash: string): SimulationResult | null {
  const entry = readCache().find((e) => e.hash === hash);
  return entry ? entry.result : null;
}

/** Métadonnées du cache (pour le label « il y a X »). */
export function getCacheMeta(hash: string): { cachedAt: number } | null {
  const entry = readCache().find((e) => e.hash === hash);
  return entry ? { cachedAt: entry.cachedAt } : null;
}

/**
 * Stocke un résultat. Déduplique par hash et garde les entrées les plus récentes
 * en tête (LRU simple).
 */
export function setCachedResult(
  hash: string,
  result: SimulationResult,
  now: number,
): void {
  const others = readCache().filter((e) => e.hash !== hash);
  writeCache([{ hash, result, cachedAt: now }, ...others]);
}

/** Vide tout le cache (utilitaire de test / debug). */
export function clearSimulationCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}
